import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Plus, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TransactionFilters, TransactionTable, TransactionForm } from "./TransactionComponents";
import { useAuth } from "../context/AuthContext";

const formatCurrency = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(value || 0);
const getTodayInputValue = () => new Date().toISOString().split('T')[0];

const buildTransactionDateTime = (dateValue, referenceIsoString) => {
  const timeSource = referenceIsoString ? new Date(referenceIsoString) : new Date();
  const [year, month, day] = dateValue.split('-').map(Number);
  const combinedUtcDate = new Date(Date.UTC(
    year,
    month - 1,
    day,
    timeSource.getUTCHours(),
    timeSource.getUTCMinutes(),
    timeSource.getUTCSeconds(),
    timeSource.getUTCMilliseconds()
  ));

  return combinedUtcDate.toISOString();
};

export default function Transactions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAssistant = user?.role === "assistant";
  const isStaff = isAdmin || isAssistant;

  const [allTransactions, setAllTransactions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [editTxId, setEditTxId] = useState(null);
  const [type, setType] = useState("income");
  const [date, setDate] = useState(getTodayInputValue());
  const [dateTimeReference, setDateTimeReference] = useState(() => new Date().toISOString());
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("TL");
  const [accountId, setAccountId] = useState("manual");
  const [accountName, setAccountName] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("none");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [accDialogOpen, setAccDialogOpen] = useState(false);
  const [manualAccName, setManualAccName] = useState("");

  const [fType, setFType] = useState("all");
  const [fAccount, setFAccount] = useState("all");
  const [fTag, setFTag] = useState("all");
  const [fPaymentMethod, setFPaymentMethod] = useState("all");
  const [fStartDate, setFStartDate] = useState("");
  const [fPaymentMethodType, setFPaymentMethodType] = useState("all");
  const [fEndDate, setFEndDate] = useState("");
  const [fSearch, setFSearch] = useState("");

  const [historyStatus, setHistoryStatus] = useState({ can_undo: false, can_redo: false });

  const incomeSummary = React.useMemo(() => {
    return transactions.reduce((total, tx) => (tx.type === "income" ? total + tx.amount : total), 0);
  }, [transactions]);

  const expenseSummary = React.useMemo(() => {
    return transactions.reduce((total, tx) => (tx.type === "expense" ? total + tx.amount : total), 0);
  }, [transactions]);

  const debtPaymentSummary = React.useMemo(() => {
    return transactions.reduce((total, tx) => (tx.type === "debt_payment" ? total + tx.amount : total), 0);
  }, [transactions]);

  const fetchHistoryStatus = useCallback(async () => {
    if (isStaff) {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/transactions/history/status`, { withCredentials: true });
        setHistoryStatus(res.data);
      } catch (e) {
        setHistoryStatus({ can_undo: false, can_redo: false });
      }
    }
  }, [BACKEND_URL, isStaff]);

  const fetchData = useCallback(async () => {
    try {
      const [txRes, accRes, tagRes, pmRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/transactions`, { withCredentials: true }),
        isStaff ? axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        axios.get(`${BACKEND_URL}/api/tags`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/payment_methods`, { withCredentials: true })
      ]);
      setAllTransactions(txRes.data);
      if (isStaff) setAccounts(accRes.data);
      setTags(tagRes.data);
      setPaymentMethods(pmRes.data);
      fetchHistoryStatus();
    } catch (e) {
      toast.error("Veriler alınamadı.");
    }
  }, [BACKEND_URL, isStaff, fetchHistoryStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // 1. Sort by latest first so opening view matches visible table order
    const sorted = [...allTransactions].sort((a, b) => {
      const timeDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
      if (timeDiff !== 0) return timeDiff;
      return (b.id || "").localeCompare(a.id || "");
    });

    // 2. Compute cumulative columns
    const balances = {};
      const cumulativeIncomes = {};
    const cumulativeExpenses = {};
    const cumulativeDebtPayments = {};
    const accountNetBalances = {};
    const withBalances = sorted.map(tx => {
      if (!balances[tx.currency]) balances[tx.currency] = 0;
      if (!cumulativeIncomes[tx.currency]) cumulativeIncomes[tx.currency] = 0;
      if (!cumulativeExpenses[tx.currency]) cumulativeExpenses[tx.currency] = 0;
      if (!cumulativeDebtPayments[tx.currency]) cumulativeDebtPayments[tx.currency] = 0;
      if (!accountNetBalances[tx.currency]) accountNetBalances[tx.currency] = 0;

      const signedAmount = tx.type === 'income' ? tx.amount : -tx.amount;

      if (tx.type === 'income') {
        cumulativeIncomes[tx.currency] += tx.amount;
      }

      if (tx.type === 'expense') {
        cumulativeExpenses[tx.currency] += tx.amount;
      }

      if (tx.type === 'debt_payment') {
        cumulativeDebtPayments[tx.currency] += tx.amount;
      }

      accountNetBalances[tx.currency] += signedAmount;

      const isNakit = tx.payment_method_type === "nakit";
      if (isNakit) {
        if (tx.type === 'income') {
          balances[tx.currency] += tx.amount;
        } else {
          balances[tx.currency] -= tx.amount;
        }
      }

      return {
        ...tx,
        cumulativeIncome: cumulativeIncomes[tx.currency],
        cumulativeExpense: cumulativeExpenses[tx.currency],
        cumulativeDebtPayment: cumulativeDebtPayments[tx.currency],
        accountNet: accountNetBalances[tx.currency],
        runningBalance: balances[tx.currency]
      };
    });

    // 3. Apply Filters
    const filtered = withBalances.filter(tx => {
      if (fType !== "all" && tx.type !== fType) return false;
      if (fAccount !== "all" && tx.account_id !== fAccount) return false;
      if (fTag !== "all" && (!tx.tags || !tx.tags.includes(fTag))) return false;
      if (fPaymentMethod !== "all" && tx.payment_method_id !== fPaymentMethod) return false;
      if (fPaymentMethodType !== "all") {
        if (tx.payment_method_type !== fPaymentMethodType) return false;
      }
      if (fStartDate && new Date(tx.date) < new Date(fStartDate)) return false;
      if (fEndDate && new Date(tx.date) > new Date(fEndDate + 'T23:59:59')) return false;
      if (fSearch) {
        const s = fSearch.toLowerCase();
        const matchDesc = tx.description?.toLowerCase().includes(s);
        const matchAcc = tx.account_name?.toLowerCase().includes(s);
        const matchAmt = tx.amount?.toString() === s;
        if (!matchDesc && !matchAcc && !matchAmt) return false;
      }
      return true;
    });

    setTransactions(filtered);
  }, [allTransactions, fType, fAccount, fTag, fPaymentMethod, fPaymentMethodType, fStartDate, fEndDate, fSearch]);


  const handleCreate = async (e) => {
    e.preventDefault();
    if (!amount) return;
    if (accountId === "manual" && !accountName.trim()) {
      toast.error("Lütfen bir cari hesap seçin veya elle bir isim girin.");
      return;
    }

    let finalAccId = accountId === "manual" ? null : accountId;
    let finalAccName = accountId === "manual" ? accountName : accounts.find(a => a.id === accountId)?.name;
    const selectedPaymentMethod = paymentMethodId === "none" ? null : paymentMethods.find(p => p.id === paymentMethodId);
    let finalPmId = selectedPaymentMethod?.id || null;
    let finalPmName = selectedPaymentMethod?.name || null;
    let finalPmType = selectedPaymentMethod?.type || "nakit";
    const finalDateTime = buildTransactionDateTime(date, editTxId ? dateTimeReference : new Date().toISOString());

    try {
      if (editTxId) {
        await axios.put(`${BACKEND_URL}/api/transactions/${editTxId}`, {
          type,
          date: finalDateTime,
          amount: parseFloat(amount),
          currency,
          account_id: finalAccId,
          account_name: finalAccName,
          payment_method_id: finalPmId,
          payment_method_name: finalPmName,
          payment_method_type: finalPmType,
          description,
          tags: selectedTags
        }, { withCredentials: true });
        toast.success("İşlem güncellendi.");
      } else {
        await axios.post(`${BACKEND_URL}/api/transactions`, {
          type,
          date: finalDateTime,
          amount: parseFloat(amount),
          currency,
          account_id: finalAccId,
          account_name: finalAccName,
          payment_method_id: finalPmId,
          payment_method_name: finalPmName,
          payment_method_type: finalPmType,
          description,
          tags: selectedTags
        }, { withCredentials: true });
        toast.success("İşlem eklendi.");
      }
      
      setAmount("");
      setDescription("");
      setAccountName("");
      setAccountId("manual");
      setManualAccName("");
      setPaymentMethodId("none");
      setSelectedTags([]);
      setDate(getTodayInputValue());
      setDateTimeReference(new Date().toISOString());
      setTxDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem eklenemedi/güncellenemedi.");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    console.log("Uploading file with type:", type);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success(`${res.data.inserted} işlem başarıyla eklendi.`);
      setTxDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel yüklenirken bir hata oluştu.");
    }
    // reset input
    e.target.value = null;
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${id}`, { withCredentials: true });
      toast.success("İşlem silindi.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi.");
    }
  };

  const handleUndo = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/undo`, {}, { withCredentials: true });
      if (res.data.status === "ok") {
        toast.success("Son işlem başarıyla geri alındı.");
        fetchData();
      } else {
        toast.error(res.data.message || "İşlem geri alınamadı.");
      }
    } catch (e) {
      toast.error("Geri alma işlemi başarısız.");
    }
  };

  const handleRedo = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/redo`, {}, { withCredentials: true });
      if (res.data.status === "ok") {
        toast.success("Geri alınan işlem başarıyla ileri alındı (tekrar uygulandı).");
        fetchData();
      } else {
        toast.error(res.data.message || "İşlem ileri alınamadı.");
      }
    } catch (e) {
      toast.error("İleri alma işlemi başarısız.");
    }
  };

  const handleExport = () => {
    let url = `${BACKEND_URL}/api/export?`;
    if (fType !== "all") url += `type=${fType}&`;
    if (fAccount !== "all") url += `account_id=${fAccount}&`;
    if (fTag !== "all") url += `tag_id=${fTag}&`;
    if (fPaymentMethod !== "all") url += `payment_method_id=${fPaymentMethod}&`;
    if (fPaymentMethodType !== "all") url += `payment_method_type=${fPaymentMethodType}&`;
    if (fStartDate) url += `start_date=${fStartDate}&`;
    if (fEndDate) url += `end_date=${fEndDate}&`;
    if (fSearch) url += `search=${fSearch}&`;
    window.open(url, '_blank');
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]);
  };

  const handleAddNewTag = async () => {
    if (!newTag.trim()) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/tags`, { name: newTag.trim(), color: "#0A0A0A" }, { withCredentials: true });
      toast.success("Yeni etiket oluşturuldu.");
      setTags([...tags, res.data]);
      setSelectedTags([...selectedTags, res.data.id]);
      setNewTag("");
    } catch (e) {
      toast.error("Etiket oluşturulamadı.");
    }
  };

  const openTxModal = (selectedType) => {
    setEditTxId(null);
    setAmount("");
    setDescription("");
    setAccountName("");
    setAccountId("manual");
    setManualAccName("");
    setPaymentMethodId("none");
    setSelectedTags([]);
    setDate(getTodayInputValue());
    setDateTimeReference(new Date().toISOString());
    setType(selectedType);
    setTxDialogOpen(true);
  };

  const handleEditClick = (tx) => {
    setEditTxId(tx.id);
    setType(tx.type);
    setDate(new Date(tx.date).toISOString().split('T')[0]);
    setDateTimeReference(tx.date);
    setAmount(tx.amount.toString());
    setCurrency(tx.currency || "TL");
    if (tx.account_id) {
      setAccountId(tx.account_id);
      setAccountName(tx.account_name || "");
      setManualAccName("");
    } else if (tx.account_name) {
      setAccountId("manual");
      setAccountName(tx.account_name);
      setManualAccName(tx.account_name);
    } else {
      setAccountId("manual");
      setAccountName("");
      setManualAccName("");
    }
    setPaymentMethodId(tx.payment_method_id || "none");
    setDescription(tx.description || "");
    setSelectedTags(tx.tags || []);
    setTxDialogOpen(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-border pb-4 gap-4">
        <h1 className="text-3xl font-heading font-bold">{isStaff ? "İşlemler" : "Hesap Ekstresi"}</h1>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              disabled={!historyStatus.can_undo}
              onClick={handleUndo}
              title="Geri Al"
              className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
              data-testid="undo-btn"
            >
              <Undo2 size={16} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={!historyStatus.can_redo}
              onClick={handleRedo}
              title="İleri Al"
              className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground mr-1"
              data-testid="redo-btn"
            >
              <Redo2 size={16} />
            </Button>
            <div className="w-px h-6 bg-border mx-1"></div>
            <Button 
              onClick={() => openTxModal('income')} 
              className="bg-income hover:bg-income/90 text-white rounded-sm h-10 px-4 md:px-5 text-[11px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="add-income-btn"
            >
              <Plus size={15} className="mr-1.5" /> Yeni Gelir Ekle
            </Button>
            <Button 
              onClick={() => openTxModal('expense')} 
              className="bg-expense hover:bg-expense/90 text-white rounded-sm h-10 px-4 md:px-5 text-[11px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="add-expense-btn"
            >
              <Plus size={15} className="mr-1.5" /> Yeni Gider Ekle
            </Button>
            <Button 
              onClick={() => openTxModal('debt_payment')} 
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-10 px-4 md:px-5 text-[11px] font-bold uppercase tracking-wider shadow-sm"
              data-testid="add-debt-btn"
            >
              <Plus size={15} className="mr-1.5" /> Yeni Borç Ödeme
            </Button>
          </div>
        )}
      </div>

      <Dialog open={txDialogOpen} onOpenChange={setTxDialogOpen}>
        <DialogContent className="sm:max-w-[750px] p-0 overflow-hidden gap-0 rounded-sm">
          <DialogHeader className="p-6 border-b border-border bg-white">
            <DialogTitle className="font-heading tracking-tight text-xl">{editTxId ? "İşlemi Düzenle" : "Yeni İşlem Ekle"}</DialogTitle>
          </DialogHeader>
          <div className="p-6 md:p-8 max-h-[75vh] overflow-y-auto bg-background">
            <TransactionForm 
              handleCreate={handleCreate} type={type} setType={setType} date={date} setDate={setDate}
              amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency}
              accountId={accountId} setAccountId={setAccountId} accountName={accountName} setAccountName={setAccountName}
              paymentMethodId={paymentMethodId} setPaymentMethodId={setPaymentMethodId} paymentMethods={paymentMethods}
              description={description} setDescription={setDescription} accounts={accounts} 
              accDialogOpen={accDialogOpen} setAccDialogOpen={setAccDialogOpen} manualAccName={manualAccName}
              setManualAccName={setManualAccName} selectedTags={selectedTags} tags={tags} toggleTag={toggleTag}
              tagDialogOpen={tagDialogOpen} setTagDialogOpen={setTagDialogOpen} newTag={newTag} 
              setNewTag={setNewTag} handleAddNewTag={handleAddNewTag} handleUpload={handleUpload}
            />
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-4 pt-2">
        <TransactionFilters 
          fType={fType} setFType={setFType}
          fAccount={fAccount} setFAccount={setFAccount} accounts={accounts}
          fTag={fTag} setFTag={setFTag} tags={tags}
          fPaymentMethod={fPaymentMethod} setFPaymentMethod={setFPaymentMethod} paymentMethods={paymentMethods}
          fPaymentMethodType={fPaymentMethodType} setFPaymentMethodType={setFPaymentMethodType}
          fStartDate={fStartDate} setFStartDate={setFStartDate}
          fEndDate={fEndDate} setFEndDate={setFEndDate}
          fSearch={fSearch} setFSearch={setFSearch}
          handleExport={handleExport}
          isAdmin={isStaff}
        />

        {fType === "income" && incomeSummary > 0 && (
          <div className="rounded-sm border border-emerald-200 bg-emerald-50/80 p-5 shadow-[0_2px_8px_rgba(16,185,129,0.08)]" data-testid="income-summary-card">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">Gelir Özeti</p>
                <p className="mt-2 text-sm text-emerald-900/80">Gelir filtresine göre ekranda görünen toplam tutar</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700/80">Toplam Gelir</p>
                <div className="mt-2 text-3xl font-bold font-heading text-emerald-700" data-testid="income-summary-value">
                  +{formatCurrency(incomeSummary)}
                </div>
              </div>
            </div>
          </div>
        )}

        {fType === "expense" && expenseSummary > 0 && (
          <div className="rounded-sm border border-red-200 bg-red-50/80 p-5 shadow-[0_2px_8px_rgba(220,38,38,0.08)]" data-testid="expense-summary-card">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-red-700">Gider Özeti</p>
                <p className="mt-2 text-sm text-red-900/80">Gider filtresine göre ekranda görünen toplam tutar</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-red-700/80">Toplam Gider</p>
                <div className="mt-2 text-3xl font-bold font-heading text-red-700" data-testid="expense-summary-value">
                  -{formatCurrency(expenseSummary)}
                </div>
              </div>
            </div>
          </div>
        )}

        {fType === "debt_payment" && debtPaymentSummary > 0 && (
          <div className="rounded-sm border border-blue-200 bg-blue-50/80 p-5 shadow-[0_2px_8px_rgba(37,99,235,0.08)]" data-testid="debt-summary-card">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">Borç Ödeme Özeti</p>
                <p className="mt-2 text-sm text-blue-900/80">Borç ödeme filtresine göre ekranda görünen toplam tutar</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700/80">Toplam Borç Ödeme</p>
                <div className="mt-2 text-3xl font-bold font-heading text-blue-700" data-testid="debt-summary-value">
                  -{formatCurrency(debtPaymentSummary)}
                </div>
              </div>
            </div>
          </div>
        )}

        <TransactionTable 
          transactions={transactions} 
          tags={tags} 
          handleDelete={handleDelete} 
          handleEditClick={handleEditClick}
          isStaff={isStaff}
          isAdmin={isAdmin}
          activeType={fType}
        />
      </div>
    </div>
  );
}
